import { buildAliasMap, buildAeIndex, matchItem } from './itemMatching';

// Shared matching pass - the single orchestration used by every catalog import
// (AdminItemCatalog, SiteUploadsPage) and, later, the ordering engine.
//
// Reads vendor_aliases + all items (paged past PostgREST's row cap) via the
// given Supabase client, runs the shared matcher, and writes
// edge_item_number / match_method / match_status for non-locked ClaimSoft rows.
// Rows with match_locked = true are skipped so manual links survive re-imports.
// Returns a human-readable summary string.
export async function runMatchingPass(supabase) {
  const { data: aliasRows, error: aliasErr } = await supabase
    .from('vendor_aliases').select('alias, canonical_name, source_system');
  if (aliasErr) throw new Error(aliasErr.message);

  /*
    **With no vendor list, this pass would undo every match in the catalog.**

    Found 6 September 2026. The rule it follows is that two items are the same
    product when their vendors provably agree, and a vendor spelling it has
    never heard of means "cannot verify". So with an empty alias table nothing
    can be confirmed and nothing can be turned down: **every one of the 1,200
    confirmed matches would be rewritten as "waiting on a yes or no", and the
    104 that genuinely need a person would become 1,304.**

    It never happened only because the table did not exist at all, so this threw
    before it got here and every caller printed the failure as a few words on the
    end of "Imported 2,500 items!". Now that the table exists, the guard has to
    be explicit: **no vendor list means nothing is re-matched**, and it says so.
  */
  if (!aliasRows || aliasRows.length === 0) {
    return ' Nothing was re-matched: there is no vendor list loaded yet, and'
      + ' without one no two items can be shown to be from the same vendor.'
      + ' Load a Vendor aliases file on Branding & Uploads.';
  }
  const aliasMap = buildAliasMap(aliasRows);

  // Page through items - PostgREST caps each request (~1000 rows).
  const allRows = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase
      .from('items')
      .select('id, source, item_number, vendor, vendor_part_number, match_locked, match_status, match_method, edge_item_number')
      .range(from, from + 999);
    if (error) throw new Error(error.message);
    allRows.push(...(data || []));
    if (!data || data.length < 1000) break;
  }

  const aeIndex = buildAeIndex(allRows.filter(r => r.source === 'edge'));
  const csRows = allRows.filter(r => r.source === 'claimsoft');

  const counts = { confirmed: 0, unverified: 0, rejected: 0, no_match: 0 };
  const updates = [];
  for (const cs of csRows) {
    if (cs.match_locked === true) continue; // never overwrite a manual mapping
    const r = matchItem(cs, aeIndex, aliasMap);
    if (counts[r.status] != null) counts[r.status]++;
    const ae = r.aeItemNumber == null ? null : r.aeItemNumber;
    const method = r.method == null ? null : r.method;
    if (cs.edge_item_number !== ae || cs.match_method !== method || cs.match_status !== r.status) {
      // Carry item_number + source so the upsert tuple is valid (both NOT NULL);
      // on id-conflict it updates only the match columns.
      updates.push({
        id: cs.id, item_number: cs.item_number, source: cs.source,
        edge_item_number: ae, match_method: method, match_status: r.status,
      });
    }
  }

  for (let i = 0; i < updates.length; i += 500) {
    const { error } = await supabase.from('items').upsert(updates.slice(i, i + 500), { onConflict: 'id' });
    if (error) throw new Error(error.message);
  }

  return ' Matched ' + csRows.length + ' ClaimSoft items (confirmed ' + counts.confirmed +
    ', unverified ' + counts.unverified + ', rejected ' + counts.rejected +
    ', no_match ' + counts.no_match + ').';
}
